import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { CALENDAR_SYNC_API_BASE_URL } from './calendar-sync-api-base-url.token';
import {
  CalendarSyncInvalidateCacheRequest,
  CalendarSyncInvalidateCacheResponse,
  CalendarSyncLoadEventsRequest,
  CalendarSyncLoadEventsResponse,
  CalendarSyncMonitoringCacheMissEntry,
  CalendarSyncMonitoringLogEntry,
  CalendarSyncMonitoringOverview,
  CalendarSyncMonitoringRequestDetails,
  CalendarSyncMonitoringRequestSummary,
  CalendarSyncResourceFilterValue,
  CalendarSyncTimelineSettings,
  CalendarSyncUpdateTimelineSettingsRequest,
  CalendarSyncWebhookSubscriptionInfo,
  CalendarSyncWebhookSubscriptionSyncResponse,
} from './models';

@Injectable({ providedIn: 'root' })
export class CalendarSyncService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(CALENDAR_SYNC_API_BASE_URL).replace(/\/$/, '');

  loadCalendarEvents(request: CalendarSyncLoadEventsRequest): Observable<CalendarSyncLoadEventsResponse> {
    return this.http.post<CalendarSyncLoadEventsResponse>(`${this.baseUrl}/calendar-events`, request);
  }

  loadO365CalendarEvents(request: CalendarSyncLoadEventsRequest): Observable<CalendarSyncLoadEventsResponse> {
    return this.loadCalendarEvents(request);
  }

  getResources(): Observable<CalendarSyncResourceFilterValue[]> {
    return this.http.get<CalendarSyncResourceFilterValue[]>(`${this.baseUrl}/resources`);
  }

  getTimelineSettings(): Observable<CalendarSyncTimelineSettings> {
    return this.http.get<CalendarSyncTimelineSettings>(`${this.baseUrl}/timeline/settings`);
  }

  updateTimelineSettings(request: CalendarSyncUpdateTimelineSettingsRequest): Observable<CalendarSyncTimelineSettings> {
    return this.http.put<CalendarSyncTimelineSettings>(`${this.baseUrl}/timeline/settings`, request);
  }

  invalidateCache(request: CalendarSyncInvalidateCacheRequest): Observable<CalendarSyncInvalidateCacheResponse> {
    return this.http.post<CalendarSyncInvalidateCacheResponse>(`${this.baseUrl}/cache/invalidate`, request);
  }

  getMonitoringOverview(): Observable<CalendarSyncMonitoringOverview> {
    return this.http.get<CalendarSyncMonitoringOverview>(`${this.baseUrl}/monitoring/overview`);
  }

  getMonitoringRequests(take?: number): Observable<CalendarSyncMonitoringRequestSummary[]> {
    let params = new HttpParams();
    if (take !== undefined && take !== null) {
      params = params.set('take', String(take));
    }

    return this.http.get<CalendarSyncMonitoringRequestSummary[]>(`${this.baseUrl}/monitoring/requests`, { params });
  }

  getMonitoringRequest(
    requestId: string,
    options?: { maxWorkItems?: number; includeLogs?: boolean }
  ): Observable<CalendarSyncMonitoringRequestDetails> {
    let params = new HttpParams();
    if (options?.maxWorkItems !== undefined && options.maxWorkItems !== null) {
      params = params.set('maxWorkItems', String(options.maxWorkItems));
    }

    if (options?.includeLogs !== undefined && options.includeLogs !== null) {
      params = params.set('includeLogs', String(options.includeLogs));
    }

    const safeRequestId = encodeURIComponent(requestId);
    return this.http.get<CalendarSyncMonitoringRequestDetails>(`${this.baseUrl}/monitoring/requests/${safeRequestId}`, {
      params,
    });
  }

  getMonitoringLogs(options?: { take?: number; requestId?: string }): Observable<CalendarSyncMonitoringLogEntry[]> {
    let params = new HttpParams();
    if (options?.take !== undefined && options.take !== null) {
      params = params.set('take', String(options.take));
    }

    if (options?.requestId) {
      params = params.set('requestId', options.requestId);
    }

    return this.http.get<CalendarSyncMonitoringLogEntry[]>(`${this.baseUrl}/monitoring/logs`, { params });
  }

  getMonitoringCacheMisses(options?: {
    take?: number;
    requestId?: string;
  }): Observable<CalendarSyncMonitoringCacheMissEntry[]> {
    let params = new HttpParams();
    if (options?.take !== undefined && options.take !== null) {
      params = params.set('take', String(options.take));
    }

    if (options?.requestId) {
      params = params.set('requestId', options.requestId);
    }

    return this.http.get<CalendarSyncMonitoringCacheMissEntry[]>(`${this.baseUrl}/monitoring/cache-misses`, { params });
  }

  clearMonitoring(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/monitoring`);
  }

  getWebhookSubscriptions(): Observable<CalendarSyncWebhookSubscriptionInfo[]> {
    return this.http.get<CalendarSyncWebhookSubscriptionInfo[]>(`${this.baseUrl}/webhook/subscriptions`);
  }

  synchronizeWebhookSubscriptions(): Observable<CalendarSyncWebhookSubscriptionSyncResponse> {
    return this.http.post<CalendarSyncWebhookSubscriptionSyncResponse>(
      `${this.baseUrl}/webhook/subscriptions/sync`,
      {}
    );
  }
}
